import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const shortcodeDescriptions = {
  checkoutSummary: "Selected tickets, quantities, costs, tax, and total",
  id: "Registration confirmation code",
  qrCode: "Registrant QR code",
  qrGuestCode: "Guest QR code",
};

export const formatShortcode = (fieldName) => `{{${fieldName}}}`;

export const ShortcodeReferencePanel = ({ fieldList = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState("Copy all");
  const shortcodes = useMemo(
    () => [...new Set(fieldList.filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [fieldList],
  );

  const handleCopyAll = async () => {
    if (!navigator.clipboard?.writeText) {
      setCopyStatus("Copy unavailable");
      return;
    }

    try {
      await navigator.clipboard.writeText(
        shortcodes.map(formatShortcode).join("\n"),
      );
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy failed");
    }
  };

  return (
    <div className="min-w-0 flex-1">
      <button
        aria-controls="shortcode-reference-panel"
        aria-expanded={isOpen}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        {isOpen ? (
          <EyeSlashIcon aria-hidden="true" className="size-5" />
        ) : (
          <EyeIcon aria-hidden="true" className="size-5" />
        )}
        {isOpen ? "Hide shortcodes" : "Show shortcodes"}
      </button>

      <Dialog
        className="relative z-[100]"
        onClose={() => setIsOpen(false)}
        open={isOpen}
      >
        <DialogBackdrop className="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" />
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <DialogPanel
              className="flex max-h-[calc(100dvh-2rem)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 text-left shadow-2xl sm:max-h-[calc(100dvh-3rem)] dark:border-zinc-700 dark:bg-zinc-900"
              id="shortcode-reference-panel"
            >
              <div className="flex shrink-0 items-start justify-between gap-4 border-b border-zinc-200 px-4 py-4 sm:px-6 dark:border-zinc-700">
                <div className="min-w-0">
                  <DialogTitle className="mb-1 text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Confirmation and email shortcodes
                  </DialogTitle>
                  <p className="mb-0 text-sm text-zinc-600 dark:text-zinc-300">
                    Use double braces in confirmation-page or confirmation-email copy.
                  </p>
                  <p className="mb-0 mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                    Wrap optional copy in {"{{if_fieldName}}"} and {"{{endif_fieldName}}"}.
                    The enclosed HTML is included only when the field has a value;
                    ticket fields require a quantity above zero.
                  </p>
                </div>
                <button
                  aria-label="Close shortcode reference"
                  className="-m-1 shrink-0 rounded-md p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                  onClick={() => setIsOpen(false)}
                  type="button"
                >
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-6">
                <div className="flex justify-end">
                  <button
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
                    onClick={handleCopyAll}
                    type="button"
                  >
                    <ClipboardDocumentIcon aria-hidden="true" className="size-5" />
                    {copyStatus}
                  </button>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {shortcodes.map((fieldName) => (
                    <div
                      className="min-w-0 rounded-md bg-zinc-100 p-2 ring-1 ring-inset ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700"
                      key={fieldName}
                    >
                      <code className="break-all text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        {formatShortcode(fieldName)}
                      </code>
                      {shortcodeDescriptions[fieldName] && (
                        <p className="mb-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                          {shortcodeDescriptions[fieldName]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
